import { isNotNil } from 'ramda'

import {
  Brand,
  getEntityBySlug,
  getProductGroupByIdQuery,
  getSingleProduct,
  ListingCategoryType,
  ProductTypes,
  Promotion} from 'modules/products'
import Link from 'shared/ui/Link/Link'
import { mapDaysOfWeek,parseDateToDDMMYYYY } from 'shared/utils/date'

import { PromotionInformation } from './types'

const getBrandSlugString = (brand: Brand): string =>
  typeof brand.slug === 'string' ? brand.slug : (brand.slug?.slug ?? '')

export const promotionTargetMap = async (
  target: string,
  productPrice: number | null,
  productName: string | null,
  discountValue: string | null,
  deliveryDate: string | null,
  freeSlot: { weekday: string; startAt: string; endAt: string }[] | null,
  productGroupId: string | null,
  token?: string
) => {
  let productInformations: {
    product: ProductTypes | null
    productGroup: ProductTypes[] | null
  } = {
    product: null,
    productGroup: null
  }

  if (isNotNil(productGroupId)) {
    const productGroup = await getProductGroupByIdQuery(token, productGroupId, {
      limit: '50'
    })

    productInformations = {
      product: null,
      productGroup: productGroup
    }
  } else if (isNotNil(productName)) {
    const product = await getSingleProduct({
      slug: productName,
      token
    })

    productInformations = {
      product: product.data.data,
      productGroup: null
    }
  }

  switch (target) {
    case 'Najtańszy produkt z produktów promocyjnych za X zł':
      return {
        jsx: (
          <>
            najtańszy produkt z dodanych produktów promocyjnych za{' '}
            <b>{productPrice} zł</b>
          </>
        ),
        ...productInformations
      }
    case 'Ustalony produkt za X zł':
      return {
        jsx: (
          <>
            {' '}
            produkt{' '}
            <b>
              <Link
                href={`/${productInformations.product?.slug?.toString() ?? ''}`}
              >
                {productInformations.product?.name}
              </Link>
            </b>{' '}
            do odebrania za <b>{productPrice} zł</b>
          </>
        ),
        ...productInformations
      }
    case 'Wybrane produkty X % tańsze':
      return {
        jsx: (
          <>
            podane niżej produkty <b>{discountValue}</b> tańsze
          </>
        ),
        ...productInformations
      }
    case 'Darmowa dostawa':
      return {
        jsx: (
          <>
            otrzymasz <b>darmową dostawę</b>
          </>
        ),
        ...productInformations
      }
    case 'Darmowa dostawa na konkretny dzień':
      return {
        jsx: (
          <>
            otrzymasz darmową dostawę na{' '}
            <b>{parseDateToDDMMYYYY(deliveryDate!)}</b>
          </>
        ),
        ...productInformations
      }
    case 'Darmowa dostawa na wybrany przedział czasowy':
      return {
        jsx: (
          <>
            otrzymasz darmową dostawę na{' '}
            <b>
              {freeSlot?.[0].weekday
                ? mapDaysOfWeek[freeSlot?.[0].weekday]
                : ''}
            </b>{' '}
            od <b>{freeSlot?.[0].startAt}</b> do <b>{freeSlot?.[0].endAt}</b>
          </>
        ),
        ...productInformations
      }
    case 'Z produktów po X zł':
      return {
        jsx: (
          <>
            otrzymasz jeden produkt z grupy produktów podanych niżej za{' '}
            <b>{productPrice}</b>
          </>
        ),
        ...productInformations
      }
    default:
      return {
        jsx: <>{target}</>,
        ...productInformations
      }
  }
}

export const promotionTypeMap = async (
  promotion: Promotion,
  token?: string
) => {
  let promotionBase: { item: unknown; type: string } = {
    item: null,
    type: ''
  }
  let promotionInformation: {
    promotionCategory: ProductTypes[] | null
    promotionBrand: ProductTypes[] | null
    promotionProduct: ProductTypes | null
    promotionGroup: ProductTypes[] | null
  } = {
    promotionCategory: null,
    promotionBrand: null,
    promotionProduct: null,
    promotionGroup: null
  }

  if (isNotNil(promotion.brandSlug)) {
    const response = await getEntityBySlug({
      slug: promotion.brandSlug,
      limit: 10,
      query: {},
      token
    })

    promotionBase = {
      item: response.data.productsBrand,
      type: 'brand'
    }
    promotionInformation = {
      promotionBrand: response.data.data as ProductTypes[],
      promotionCategory: null,
      promotionProduct: null,
      promotionGroup: null
    }
  } else if (isNotNil(promotion.productCategorySlug)) {
    const response = await getEntityBySlug({
      slug: promotion.productCategorySlug,
      token,
      limit: 10,
      query: {}
    })

    promotionBase = {
      item: response.data.productCategory,
      type: 'category'
    }
    promotionInformation = {
      promotionBrand: null,
      promotionCategory: response.data.data as ProductTypes[],
      promotionProduct: null,
      promotionGroup: null
    }
  } else if (isNotNil(promotion.sourceProductGroupId)) {
    const productGroup = await getProductGroupByIdQuery(
      token,
      promotion.sourceProductGroupId,
      { limit: '50' }
    )

    promotionBase = {
      item: productGroup,
      type: 'group'
    }
    promotionInformation = {
      promotionBrand: null,
      promotionCategory: null,
      promotionProduct: null,
      promotionGroup: productGroup
    }
  }

  switch (promotion.condition) {
    case 'Kup produkty za X zł':
      return {
        jsx: (
          <>
            Przy zakupie produktów za <b>{promotion.minProductsValue} zł </b>
            {promotionBase.item && promotionBase.type === 'category' && (
              <>
                z kategorii{' '}
                <b>
                  <Link href={(promotionBase.item as ListingCategoryType).totalSlug}>
                    {(promotionBase.item as ListingCategoryType).name}
                  </Link>
                </b>
              </>
            )}
            {promotionBase.item && promotionBase.type === 'brand' && (
              <>
                od Wytwórcy{' '}
                <b>
                  <Link
                    href={`/${getBrandSlugString(promotionBase.item as Brand)}`}
                  >
                    {(promotionBase.item as Brand).name}
                  </Link>
                </b>
              </>
            )}
            {promotionBase.item && promotionBase.type === 'group' && (
              <> z produktów podanych niżej, </>
            )}
          </>
        ),
        promotionInformation
      }
    case 'Kup X produktów':
      return {
        jsx: (
          <>
            Przy zakupie <b>{promotion.minProductsQuantity}</b> sztuk{' '}
            {promotionBase.item && promotionBase.type === 'category' && (
              <>
                z kategorii{' '}
                <b>
                  <Link
                    href={`/${(promotionBase.item as ListingCategoryType).totalSlug}`}
                  >
                    {(promotionBase.item as ListingCategoryType).name}
                  </Link>
                </b>
                ,
              </>
            )}
            {promotionBase.item && promotionBase.type === 'brand' && (
              <>
                {' '}
                produktów od Wytwórcy{' '}
                <b>
                  <Link
                    href={`/${getBrandSlugString(promotionBase.item as Brand)}`}
                  >
                    {(promotionBase.item as Brand).name}
                  </Link>
                </b>
                ,
              </>
            )}
            {promotionBase.item && promotionBase.type === 'group' && (
              <> produktów podanych niżej, </>
            )}
          </>
        ),
        promotionInformation
      }
    default:
      return {
        jsx: <>{promotion.condition}</>,
        promotionInformation
      }
  }
}

const promotionCache: Record<
  string,
  Record<string, PromotionInformation>
> = {}

export const getPromotionInformation = async (
  promotion: Promotion,
  token = ''
): Promise<PromotionInformation> => {
  if (promotionCache[promotion.id]?.[token]) {
    return promotionCache[promotion.id][token]
  }

  const targetDescription = await promotionTargetMap(
    promotion.type,
    promotion.productPriceGross,
    promotion.productId,
    promotion.discountValue,
    promotion.freeDeliveryDate,
    promotion.freeSlots,
    promotion.targetProductGroupId,
    token
  )
  const conditionDescription = await promotionTypeMap(promotion, token)

  const promotionInfo = {
    conditionDescription: conditionDescription.jsx,
    targetDescription: targetDescription.jsx,
    targetProduct: targetDescription.product,
    targetProductGroup: targetDescription.productGroup,
    promotionCategory:
      conditionDescription.promotionInformation.promotionCategory,
    promotionBrand: conditionDescription.promotionInformation.promotionBrand,
    promotionProduct:
      conditionDescription.promotionInformation.promotionProduct,
    promotionGroup: conditionDescription.promotionInformation.promotionGroup
  }

  if (!promotionCache[promotion.id]) {
    promotionCache[promotion.id] = {}
  }
  promotionCache[promotion.id][token] = promotionInfo

  return promotionInfo
}
